import React from 'react';

import { useGraphQLQuery } from '@betterment/js-runtime';
import { Card, HStack, VStack } from '@betterment/samba-react';
import { CardLoader, ErrorBoundary } from '@betterment/shared-components';
import { useRequiredParam } from '@betterment/shared-components/src/hooks/useRequiredParam';

import { CashBalanceRow } from './components/CashBalanceRow';
import { EnvelopeAccountTransactionOptionsDocument } from './graphql/envelopeAccountTransactionOptions.generated';

import {
  DepositButton,
  OtherTransactionOptionsMenu,
} from '../../../Shared/components/transaction-buttons/TransactionButtons';

const Content: React.FC = () => {
  const accountId = useRequiredParam('accountId');

  const { data } = useGraphQLQuery(EnvelopeAccountTransactionOptionsDocument, {
    variables: { id: accountId },
    throwOnError: true,
  });

  if (!data) return <CardLoader />;

  const { account } = data;
  const transactionOptions = 'transactionOptions' in account ? account.transactionOptions : [];

  const subAccounts = (() => {
    switch (account.__typename) {
      case 'CashAccount':
      case 'DirectTradingAccount':
        return [{ id: account.legacySubAccountId, taxationType: account.legalAccount.taxationType }];
      case 'ManagedAccount':
        return account.subAccounts.map(sa => ({
          id: sa.legacySubAccountId,
          taxationType: sa.legalAccount.taxationType,
        }));
      default:
        return [];
    }
  })();

  return (
    <Card>
      <Card.Content
        css={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: 'space.300',
        }}
      >
        {account.__typename === 'DirectTradingAccount' ? (
          <VStack css={{ gap: 'space.300', width: '100%' }}>
            <CashBalanceRow
              account={account}
              transactionOptions={transactionOptions}
              subAccounts={subAccounts}
              isCompact={true}
            />
          </VStack>
        ) : (
          <HStack css={{ gap: 'space.150', width: '100%', '& > *': { flex: 1 } }}>
            <OtherTransactionOptionsMenu
              transactionOptions={transactionOptions}
              subAccounts={subAccounts}
              isCompact={true}
            />
            <DepositButton transactionOptions={transactionOptions} subAccounts={subAccounts} isCompact={true} />
          </HStack>
        )}
      </Card.Content>
    </Card>
  );
};

export const TransferDepositCard: React.FC = () => (
  <ErrorBoundary fallbackType="none">
    <Content />
  </ErrorBoundary>
);
